const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/modules/CIS4m/components/CIS4mRoot.tsx';
import * as React from 'react';
import { forwardRef, ReactNode, useMemo } from 'react';
import { css, cx } from '@emotion/css';
import { useCIS4mData, useConnectToChildCIS4mComponents } from '../hooks';
import { colors } from '@mui/material';
import { CIS4mContext } from '../context';
import { useTheme } from '@mui/material/styles';
import { FREEZE_SCROLLING_CLASS } from '../../../utils/styling';
import { TextFieldVariants } from '@mui/material/TextField/TextField';
import { CIS4mData } from '../types';

function useCSSClasses() {
    const theme = useTheme();
    return useMemo(
        () => ({
            cssContent: css`
                flex: 1 1 0;
                min-height: 0;
                background-color: ${colors.grey['50']};
                padding: ${theme.spacing(2)};
                display: flex;
                flex-direction: column;
                overflow-y: auto;
            `,
        }),
        [theme]
    );
}

const CIS4mDataGuard = (props: { children: ReactNode }) => {
    const cis4mData = useCIS4mData();
    if (!cis4mData) return null;
    return props.children;
};

interface Props {
    fieldVariant?: TextFieldVariants;
    formData: CIS4mData;
    className?: string;
    locked?: boolean;
}

export const CIS4mRoot = forwardRef((props: Props, ref: any) => {
    const { formData, className, locked = false } = props;
    const classes = useCSSClasses();

    const child = formData.renderForm();
    const referencedChildren = useConnectToChildCIS4mComponents(ref, child);
    const fieldVariant = props.fieldVariant ?? 'standard';

    return (
        <CIS4mContext.Provider value={{ formId: formData.id, style: { variant: fieldVariant }, locked }}>
            <CIS4mDataGuard>
                <div className={cx('CIS4mRoot', classes.cssContent, FREEZE_SCROLLING_CLASS, className)}>
                    {referencedChildren}
                </div>
            </CIS4mDataGuard>
        </CIS4mContext.Provider>
    );
});

CIS4mRoot.displayName = 'CIS4mRoot';
